/**
 * BOQ Form Component
 * Form for creating a new Bill of Quantities
 */

import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { queryKeys } from "@/lib/query-keys";
import {
	type BOQStarterPreview,
	type BOQStarterTemplate,
	createBOQ,
	generateBOQFromStarterTemplate,
	getBOQStarterTemplates,
	previewBOQStarterTemplate,
	type StarterFailureDetails,
} from "../../services/boq/boq-api.service";
import { unpricedGroupText, warningText } from "./unpriced-copy";

type CreateMode = "blank" | "starter";

function newIdempotencyKey(): string {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function BOQForm() {
	const { t, i18n } = useTranslation();
	const { projectId } = useParams<{ projectId: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const [title, setTitle] = useState("");
	const [notes, setNotes] = useState("");
	const [mode, setMode] = useState<CreateMode>(
		searchParams.get("mode") === "starter" ? "starter" : "blank",
	);
	const [templates, setTemplates] = useState<BOQStarterTemplate[]>([]);
	const [selectedTemplateId, setSelectedTemplateId] = useState("");
	const [preview, setPreview] = useState<BOQStarterPreview | null>(null);
	const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
	const [isPreviewing, setIsPreviewing] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Lines under `error`: for a template that can't be priced, one per reason.
	const [errorDetails, setErrorDetails] = useState<string[]>([]);
	// Per-render idempotency key — regenerated on a fresh page load. Submitting
	// the same form (e.g. accidental double-click) reuses the key so the
	// backend dedupes within its 5-minute window.
	const idempotencyKey = useRef<string>(newIdempotencyKey());

	useEffect(() => {
		if (!projectId || mode !== "starter") return;
		let cancelled = false;
		const loadTemplates = async () => {
			setIsLoadingTemplates(true);
			const result = await getBOQStarterTemplates(projectId);
			if (cancelled) return;
			if (!result.success) {
				toast.error(result.error ?? t("boq.starter.loadError"));
				setIsLoadingTemplates(false);
				return;
			}
			setTemplates(result.templates);
			if (result.templates[0]) {
				setSelectedTemplateId(result.templates[0].id);
			}
			setIsLoadingTemplates(false);
		};
		loadTemplates();
		return () => {
			cancelled = true;
		};
	}, [mode, projectId, t]);

	// The template currently chosen, read after a preview's await: a preview
	// that returns after the designer picked another template is dropped.
	const selectedTemplateRef = useRef(selectedTemplateId);
	selectedTemplateRef.current = selectedTemplateId;

	const handlePreview = async () => {
		if (!projectId || !selectedTemplateId) return;
		const requestedTemplateId = selectedTemplateId;
		setError(null);
		setErrorDetails([]);
		setIsPreviewing(true);
		try {
			const result = await previewBOQStarterTemplate(
				projectId,
				requestedTemplateId,
			);
			if (selectedTemplateRef.current !== requestedTemplateId) return;
			if (result.success && result.preview) {
				setPreview(result.preview);
			} else {
				setPreview(null);
				setError(
					starterFailureMessage(result) || "Failed to preview starter BOQ",
				);
				setErrorDetails(starterFailureDetails(result));
			}
		} finally {
			setIsPreviewing(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!projectId) return;

		setIsSubmitting(true);
		setError(null);
		setErrorDetails([]);

		try {
			const result =
				mode === "starter" && selectedTemplateId
					? await generateBOQFromStarterTemplate(
							projectId,
							selectedTemplateId,
							{
								title: title.trim() || undefined,
								notes: notes.trim() || undefined,
								idempotencyKey: idempotencyKey.current,
							},
						)
					: await createBOQ(projectId, {
							title: title.trim() || undefined,
							notes: notes.trim() || undefined,
						});

			if (result.success) {
				void queryClient.invalidateQueries({
					queryKey: queryKeys.projects.boq(projectId),
				});
				toast.success(t("boq.create.success"));
				navigate(`/boqs/${result.boq?.id}`);
			} else {
				const message = starterFailureMessage(result) || t("boq.create.error");
				setError(message);
				setErrorDetails(starterFailureDetails(result));
				toast.error(message);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	/**
	 * A malformed template rule comes back as an internal message naming a
	 * template item id, which tells a designer nothing. Say what it means.
	 */
	function starterFailureMessage(
		result: StarterFailureDetails & { error?: string },
	): string | undefined {
		if (result.code === "malformed_rule") {
			return t("boq.starter.malformedTemplate");
		}
		if (result.code === "template_unpriced") {
			return t("boq.starter.unpriced.title");
		}
		return result.error;
	}

	/**
	 * For a template none of whose lines has a price: how many lines each
	 * reason covers, and who can fix it.
	 */
	function starterFailureDetails(
		result: StarterFailureDetails & { success: boolean },
	): string[] {
		if (result.code !== "template_unpriced" || !result.unpriced) return [];
		const lines = result.unpriced.groups.map((group) =>
			unpricedGroupText(t, i18n.language, group),
		);
		if (result.unpriced.unavailableLines > 0) {
			lines.push(
				t("boq.starter.unpriced.unavailable", {
					count: result.unpriced.unavailableLines,
				}),
			);
		}
		return lines;
	}

	const selectedTemplate = useMemo(
		() => templates.find((template) => template.id === selectedTemplateId),
		[templates, selectedTemplateId],
	);

	return (
		<div className="max-w-3xl mx-auto space-y-6">
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => navigate(`/projects/${projectId}`)}
					style={{ color: "var(--color-dashboard-muted)" }}
				>
					<ArrowLeft className="h-5 w-5" />
				</Button>
				<div>
					<h1
						className="text-2xl"
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontWeight: 500,
							color: "var(--color-dashboard-text)",
							letterSpacing: "-0.01em",
						}}
					>
						{t("boq.create.title")}
					</h1>
					<p
						className="text-sm"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("boq.create.subtitle")}
					</p>
				</div>
			</div>

			<div
				className="rounded-2xl"
				style={{
					background: "var(--color-dashboard-paper)",
					border: "1px solid var(--color-dashboard-border)",
				}}
			>
				<div
					className="border-b px-6 py-4"
					style={{ borderColor: "var(--color-dashboard-border)" }}
				>
					<div className="flex items-center gap-2">
						<FileText
							className="h-5 w-5"
							style={{ color: "var(--color-dashboard-accent-dark)" }}
						/>
						<span
							className="text-lg"
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("boq.create.cardTitle")}
						</span>
					</div>
					<p
						className="text-sm"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("boq.create.cardDesc")}
					</p>
				</div>
				<div className="p-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{error && (
							<div
								className="rounded-lg border p-4"
								style={{
									borderColor: "var(--color-dashboard-accent)",
									background: "var(--color-dashboard-warm-100)",
								}}
							>
								<p
									className="text-sm"
									style={{ color: "var(--color-dashboard-accent-dark)" }}
								>
									{error}
								</p>
								{errorDetails.length > 0 && (
									<ul
										className="mt-2 list-disc space-y-1 pl-5 text-sm"
										style={{ color: "var(--color-dashboard-accent-dark)" }}
									>
										{errorDetails.map((line) => (
											<li key={line}>{line}</li>
										))}
									</ul>
								)}
							</div>
						)}

						<div
							className="grid grid-cols-2 gap-2 rounded-lg p-1"
							style={{ background: "var(--color-dashboard-divider)" }}
						>
							<Button
								type="button"
								variant={mode === "blank" ? "default" : "ghost"}
								onClick={() => {
									setMode("blank");
									setPreview(null);
									setError(null);
								}}
								style={
									mode === "blank"
										? {
												background: "var(--color-dashboard-accent)",
												color: "var(--color-dashboard-on-fill)",
											}
										: {
												background: "transparent",
												color: "var(--color-dashboard-muted)",
											}
								}
							>
								<FileText className="mr-2 h-4 w-4" />
								{t("boq.create.blank")}
							</Button>
							<Button
								type="button"
								variant={mode === "starter" ? "default" : "ghost"}
								onClick={() => {
									setMode("starter");
									setError(null);
								}}
								style={
									mode === "starter"
										? {
												background: "var(--color-dashboard-accent)",
												color: "var(--color-dashboard-on-fill)",
											}
										: {
												background: "transparent",
												color: "var(--color-dashboard-muted)",
											}
								}
							>
								<Sparkles className="mr-2 h-4 w-4" />
								{t("boq.create.starter")}
							</Button>
						</div>

						<div className="space-y-2">
							<Label
								htmlFor="title"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("boq.create.boqTitle")}
							</Label>
							<Input
								id="title"
								placeholder={t("boq.create.boqTitlePlaceholder")}
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								maxLength={200}
								style={{
									borderColor: "var(--color-dashboard-border)",
									background: "var(--color-dashboard-paper)",
									color: "var(--color-dashboard-text)",
								}}
							/>
							<p
								className="text-xs"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("boq.create.boqTitleHint")}
							</p>
						</div>

						<div className="space-y-2">
							<Label
								htmlFor="notes"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("boq.create.boqNotes")}
							</Label>
							<Textarea
								id="notes"
								placeholder={t("boq.create.boqNotesPlaceholder")}
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								rows={4}
								maxLength={5000}
								style={{
									borderColor: "var(--color-dashboard-border)",
									background: "var(--color-dashboard-paper)",
									color: "var(--color-dashboard-text)",
								}}
							/>
							<p
								className="text-xs"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("boq.create.boqNotesHint")}
							</p>
						</div>

						{mode === "starter" && (
							<div
								className="space-y-4 rounded-lg border p-4"
								style={{ borderColor: "var(--color-dashboard-border)" }}
							>
								<div>
									<h3
										className="font-semibold"
										style={{ color: "var(--color-dashboard-text)" }}
									>
										{t("boq.starter.sectionTitle")}
									</h3>
									<p
										className="text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{t("boq.starter.sectionDescription")}
									</p>
								</div>

								{isLoadingTemplates ? (
									<SkeletonRows
										label={t("boq.starter.loading")}
										rows={2}
										rowClassName="h-10"
									/>
								) : templates.length === 0 ? (
									<p
										className="text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{t("boq.starter.empty")}
									</p>
								) : (
									<>
										<div className="grid gap-3 sm:grid-cols-2">
											{templates.map((template) => (
												<button
													key={template.id}
													type="button"
													className="rounded-lg border p-4 text-left transition-colors"
													style={
														selectedTemplateId === template.id
															? {
																	borderColor: "var(--color-dashboard-accent)",
																	background: "var(--color-dashboard-warm-100)",
																}
															: {
																	borderColor: "var(--color-dashboard-border)",
																	background: "var(--color-dashboard-paper)",
																}
													}
													onClick={() => {
														setSelectedTemplateId(template.id);
														setPreview(null);
														// Clear any failure shown for the
														// previous selection.
														setError(null);
													}}
												>
													<div
														className="font-medium"
														style={{ color: "var(--color-dashboard-text)" }}
													>
														{template.name}
													</div>
													{template.description && (
														<p
															className="mt-1 text-sm"
															style={{ color: "var(--color-dashboard-muted)" }}
														>
															{template.description}
														</p>
													)}
												</button>
											))}
										</div>

										<div className="flex justify-end">
											<Button
												type="button"
												variant="outline"
												onClick={handlePreview}
												disabled={!selectedTemplateId || isPreviewing}
												style={{
													borderColor: "var(--color-dashboard-border)",
													color: "var(--color-dashboard-text)",
												}}
											>
												{isPreviewing
													? t("boq.starter.previewing")
													: t("boq.starter.preview")}
											</Button>
										</div>

										{selectedTemplate && !preview && (
											<p
												className="text-sm"
												style={{ color: "var(--color-dashboard-muted)" }}
											>
												{t("boq.starter.selected", {
													name: selectedTemplate.name,
												})}
											</p>
										)}
									</>
								)}

								{preview && (
									<div
										className="space-y-4 rounded-lg p-4"
										style={{ background: "var(--color-dashboard-surface)" }}
									>
										<div className="flex items-center justify-between">
											<div>
												<h4
													className="font-medium"
													style={{ color: "var(--color-dashboard-text)" }}
												>
													{t("boq.starter.summaryTitle")}
												</h4>
												<p
													className="text-sm"
													style={{ color: "var(--color-dashboard-muted)" }}
												>
													{t("boq.starter.summaryCounts", {
														sections: preview.sections.length,
														items: preview.sections.reduce(
															(count, section) => count + section.items.length,
															0,
														),
													})}
												</p>
											</div>
											<div className="text-right text-sm">
												<div style={{ color: "var(--color-dashboard-muted)" }}>
													{t("boq.starter.estimatedTotal")}
												</div>
												<div
													className="font-semibold"
													style={{ color: "var(--color-dashboard-text)" }}
												>
													{new Intl.NumberFormat(i18n.language, {
														style: "currency",
														currency: "INR",
													}).format(preview.estimatedTotal / 100)}
												</div>
											</div>
										</div>

										{preview.assumptions.length > 0 && (
											<div>
												<div
													className="mb-2 text-sm font-medium"
													style={{ color: "var(--color-dashboard-text)" }}
												>
													{t("boq.starter.assumptionsTitle")}
												</div>
												<ul
													className="space-y-1 text-sm"
													style={{ color: "var(--color-dashboard-muted)" }}
												>
													{preview.assumptions.map((assumption) => (
														<li
															key={`${assumption.label}-${assumption.source}`}
														>
															{assumption.label}: {assumption.value}
														</li>
													))}
												</ul>
											</div>
										)}

										{preview.warnings.length > 0 && (
											<div
												className="rounded-md border p-3 text-sm"
												style={{
													borderColor: "var(--color-dashboard-border)",
													background: "var(--color-dashboard-warm-100)",
													color: "var(--color-dashboard-accent-dark)",
												}}
											>
												<div className="font-medium">
													{t("boq.starter.warningsTitle")}
												</div>
												<ul className="mt-1 list-disc space-y-1 pl-5">
													{preview.warnings.map((warning, index) => (
														// biome-ignore lint/suspicious/noArrayIndexKey: static warning list, codes may repeat and order is stable
														<li key={`${warning.code}-${index}`}>
															{warningText(t, i18n.language, warning)}
														</li>
													))}
												</ul>
											</div>
										)}
									</div>
								)}
							</div>
						)}

						<div className="flex gap-3 justify-end">
							<Button
								type="button"
								variant="outline"
								onClick={() => navigate(-1)}
								disabled={isSubmitting}
								style={{
									borderColor: "var(--color-dashboard-border)",
									color: "var(--color-dashboard-muted)",
								}}
							>
								{t("common.cancel")}
							</Button>
							<Button
								type="submit"
								disabled={
									isSubmitting ||
									(mode === "starter" &&
										(!selectedTemplateId || templates.length === 0))
								}
								style={{
									background: "var(--color-dashboard-accent)",
									color: "var(--color-dashboard-on-fill)",
								}}
							>
								{isSubmitting
									? t("common.creating")
									: mode === "starter"
										? t("boq.starter.generate")
										: t("boq.createBoq")}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
